import React, { useState, useEffect } from 'react';
import { AdminLayout } from '../components/layout/AdminLayout';
import { categoryAPI, subCategoryAPI } from '../lib/api';
import { Plus, Pencil, Trash2, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { toast } from 'sonner';

const SubCategories = () => {
  const [subCategories, setSubCategories] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedSubCategory, setSelectedSubCategory] = useState(null);
  const [saving, setSaving] = useState(false);
  const [filterCategory, setFilterCategory] = useState('all');
  const [formData, setFormData] = useState({
    name: '',
    category_id: '',
    description: '',
    default_percentage: 10,
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [categoriesRes, subCategoriesRes] = await Promise.all([
        categoryAPI.getAll(),
        subCategoryAPI.getAll(),
      ]);
      setCategories(categoriesRes.data);
      setSubCategories(subCategoriesRes.data);
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (subCategory = null) => {
    if (subCategory) {
      setSelectedSubCategory(subCategory);
      setFormData({
        name: subCategory.name,
        category_id: subCategory.category_id,
        description: subCategory.description,
        default_percentage: subCategory.default_percentage,
      });
    } else {
      setSelectedSubCategory(null);
      setFormData({ name: '', category_id: categories[0]?.id || '', description: '', default_percentage: 10 });
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSelectedSubCategory(null);
    setFormData({ name: '', category_id: '', description: '', default_percentage: 10 });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name.trim()) {
      toast.error('Sub-category name is required');
      return;
    }

    if (!formData.category_id) {
      toast.error('Please select a category');
      return;
    }

    setSaving(true);
    try {
      if (selectedSubCategory) {
        await subCategoryAPI.update(selectedSubCategory.id, formData);
        toast.success('Sub-category updated successfully');
      } else {
        await subCategoryAPI.create(formData);
        toast.success('Sub-category created successfully');
      }
      fetchData();
      handleCloseDialog();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save sub-category');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedSubCategory) return;

    try {
      await subCategoryAPI.delete(selectedSubCategory.id);
      toast.success('Sub-category deleted successfully');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete sub-category');
    } finally {
      setDeleteDialogOpen(false);
      setSelectedSubCategory(null);
    }
  };

  const openDeleteDialog = (subCategory) => {
    setSelectedSubCategory(subCategory);
    setDeleteDialogOpen(true);
  };

  const filteredSubCategories = filterCategory === 'all' 
    ? subCategories 
    : subCategories.filter(sc => sc.category_id === filterCategory);

  return (
    <AdminLayout>
      <div className="animate-fade-in" data-testid="subcategories-page">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="font-serif text-3xl font-medium text-[#1A1A1A] tracking-tight">
              Sub-Categories
            </h1>
            <p className="text-[#585858] mt-2">
              Manage sub-categories within each main category
            </p>
          </div>
          <Button
            onClick={() => handleOpenDialog()}
            disabled={categories.length === 0}
            className="bg-[#69111F] hover:bg-[#500d18] text-[#F7F0E8] font-medium uppercase tracking-wide rounded-sm"
            data-testid="add-subcategory-btn"
          >
            <Plus className="w-5 h-5 mr-2" />
            Add Sub-Category
          </Button>
        </div>

        {/* Filter */}
        <div className="mb-6">
          <div className="flex items-center gap-4">
            <Label className="text-[#585858]">Filter by Category:</Label>
            <Select value={filterCategory} onValueChange={setFilterCategory}>
              <SelectTrigger className="w-64 border-[#E6DCD3]" data-testid="filter-category-select">
                <SelectValue placeholder="All Categories" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Categories</SelectItem>
                {categories.map((cat) => (
                  <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Table */}
        <div className="bg-white border border-[#E6DCD3] rounded-sm overflow-hidden">
          {loading ? (
            <div className="p-12 text-center">
              <Loader2 className="w-8 h-8 animate-spin mx-auto text-[#69111F]" />
            </div>
          ) : categories.length === 0 ? (
            <div className="p-12 text-center text-[#585858]">
              <p>Please create categories first before adding sub-categories.</p>
            </div>
          ) : filteredSubCategories.length === 0 ? (
            <div className="p-12 text-center text-[#585858]">
              <p>No sub-categories found. Create your first sub-category to get started.</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="bg-[#F2EBE3] hover:bg-[#F2EBE3]">
                  <TableHead className="font-serif text-[#69111F] font-medium">Name</TableHead>
                  <TableHead className="font-serif text-[#69111F] font-medium">Category</TableHead>
                  <TableHead className="font-serif text-[#69111F] font-medium">Description</TableHead>
                  <TableHead className="font-serif text-[#69111F] font-medium text-right">Default %</TableHead>
                  <TableHead className="font-serif text-[#69111F] font-medium text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredSubCategories.map((subCategory) => (
                  <TableRow key={subCategory.id} className="hover:bg-[#FDFBF8]">
                    <TableCell className="font-medium text-[#1A1A1A]">{subCategory.name}</TableCell>
                    <TableCell>
                      <span className="px-3 py-1 bg-[#F2EBE3] text-[#69111F] text-sm rounded-sm">
                        {subCategory.category_name}
                      </span>
                    </TableCell>
                    <TableCell className="text-[#585858]">{subCategory.description || '-'}</TableCell>
                    <TableCell className="text-right font-serif text-[#69111F]">
                      {subCategory.default_percentage}%
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleOpenDialog(subCategory)}
                          className="text-[#69111F] hover:bg-[#69111F]/10"
                          data-testid={`edit-subcategory-${subCategory.id}`}
                        >
                          <Pencil className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => openDeleteDialog(subCategory)}
                          className="text-red-600 hover:bg-red-50"
                          data-testid={`delete-subcategory-${subCategory.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </div>

        {/* Create/Edit Dialog */}
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="sm:max-w-md bg-white">
            <DialogHeader>
              <DialogTitle className="font-serif text-xl text-[#1A1A1A]">
                {selectedSubCategory ? 'Edit Sub-Category' : 'Create Sub-Category'}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit}>
              <div className="space-y-4 py-4">
                <div className="space-y-2">
                  <Label htmlFor="category" className="text-[#1A1A1A]">Category *</Label>
                  <Select 
                    value={formData.category_id} 
                    onValueChange={(value) => setFormData({ ...formData, category_id: value })}
                  >
                    <SelectTrigger className="border-[#E6DCD3]" data-testid="subcategory-category-select">
                      <SelectValue placeholder="Select a category" />
                    </SelectTrigger>
                    <SelectContent>
                      {categories.map((cat) => (
                        <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="name" className="text-[#1A1A1A]">Name *</Label>
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="e.g., Flowers"
                    className="border-[#E6DCD3] focus:border-[#69111F]"
                    data-testid="subcategory-name-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="description" className="text-[#1A1A1A]">Description</Label>
                  <Textarea
                    id="description"
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    placeholder="Brief description of this sub-category"
                    className="border-[#E6DCD3] focus:border-[#69111F] min-h-[80px]"
                    data-testid="subcategory-description-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="percentage" className="text-[#1A1A1A]">Default Percentage (%)</Label>
                  <Input
                    id="percentage"
                    type="number"
                    min="0"
                    max="100"
                    value={formData.default_percentage}
                    onChange={(e) => setFormData({ ...formData, default_percentage: parseFloat(e.target.value) || 0 })}
                    className="border-[#E6DCD3] focus:border-[#69111F]"
                    data-testid="subcategory-percentage-input"
                  />
                </div>
              </div>
              <DialogFooter>
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleCloseDialog}
                  className="border-[#E6DCD3]"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={saving}
                  className="bg-[#69111F] hover:bg-[#500d18] text-[#F7F0E8]"
                  data-testid="save-subcategory-btn"
                >
                  {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                  {selectedSubCategory ? 'Update' : 'Create'}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* Delete Confirmation Dialog */}
        <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
          <AlertDialogContent className="bg-white">
            <AlertDialogHeader>
              <AlertDialogTitle className="font-serif text-[#1A1A1A]">
                Delete Sub-Category
              </AlertDialogTitle>
              <AlertDialogDescription className="text-[#585858]">
                Are you sure you want to delete "{selectedSubCategory?.name}"? This action cannot be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel className="border-[#E6DCD3]">Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={handleDelete}
                className="bg-red-600 hover:bg-red-700 text-white"
                data-testid="confirm-delete-subcategory-btn"
              >
                Delete
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </AdminLayout>
  );
};

export default SubCategories;
